import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import logoFarmlogics from "../assets/img/Logo-farmlogics.webp";
import "./SidebarNew.css";
import SVGIcon from "./DynamicIcon";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { MenuSettingsContext } from "./MenuSettingsContext";
import { useLoading } from "../shared/Loader/LoadingContext";
import { toast } from "react-toastify";
import { setFilterData, setSearchData } from "../redux/tableFilter";
import { setLastClickedMenu } from "../redux/sidebarSlice";
import Cookies from "js-cookie";
import authAxios from '../services/Api';
import TokenService from "../services/token.service";
import { addLog } from '../store/auth-store';
import { resetSidebar } from '../redux/sidebarSlice';
import { expiryMsgStore } from '../services/Createapiinstance';
import { IMAGE } from '../services/config';
import { BASE_API_URL } from '../services/config';
import ReactDOM from "react-dom";
import ConfirmModal from "../shared/ConfirmModal/ConfirmModal";
import TreeView from '@mui/lab/TreeView';
import TreeItem from '@mui/lab/TreeItem';
import IframeViewer from './IframeViewer';

const POPUP_ONLY_TYPES = [
  "design",
  "setting",
  "desginproject",
  "designformgroup",
  "designworkflow",
  "designtask",
  "dashboardlist",
  "designapibuilder",
  "designpdfbuilder",
  "user-management",
  "masterdata",
  "allocation",
  "filtersetting",
  "roles-permission",
  "generalsetting",
  "menu_order",
  "dynamicapi",
  "audit-log",
  "scheduledapis",
  "integrations",
];
const NO_ARROW_TYPES = ["design", "setting"];

const SidebarNew = ({ collapsed, setCollapsed, onMenuClose }) => {

  const [showProjectTag, setShowProjectTag] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { menuUpdate } = useContext(MenuSettingsContext);
  const [treeData, setTreeData] = useState([]);
  const [expandedNodes, setExpandedNodes] = useState([]);
  const [baseParentId, setBaseParentId] = useState('home');
  const [previousPath, setPreviousPath] = useState([]);
  const [activeProjectId, setActiveProjectId] = useState(null);
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
  const { setIsGlobalLoading } = useLoading();
  const [selectedProject, setSelectedProject] = useState(null);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [showIframe, setShowIframe] = useState(false);
  const [iframeConfig, setIframeConfig] = useState({ url: '', title: '', token: null, refreshToken: null });
  const hideAddIcon = ["design", "setting"].includes(selectedProject?.type);
  const POPUP_HEIGHT = 350; // matches .project-tag-scroll max-height
  const POPUP_MARGIN = 12;

  const sidebarRef = useRef(null);
  const popupRef = useRef(null);
  
  // decryptData removed — using TokenService.decryptData (key centralised in token.service.js)
  const lastClickedMenu = useSelector((state) => state.sidebar.lastClickedMenu);
  const orgId = useMemo(() => TokenService.decryptData(Cookies.get("org_id")), []);
  const logo = useMemo(() => TokenService.decryptData(Cookies.get("org_logo")), []);
  const org_logo = useMemo(
    () => (logo !== null && logo !== '' && logo !== undefined) ? `${BASE_API_URL}${IMAGE}${logo}` : null,
    [logo]
  );


  const handleIframeBack = () => {
    setShowIframe(false);
    setIframeConfig({ url: '', title: '', token: null });
    setShowProjectTag(false);
    setSelectedProject(null);
    setActiveProjectId('home');
    navigate('/dashboard');
  };

  // Fetches a fresh embed token from the backend (which reads the HttpOnly
  // accessToken cookie server-side) then opens the IframeViewer.
  // The token is passed to the embedded app securely via postMessage.
  const openInIframe = async (url, title) => {
    try {
      const response = await authAxios.get('/api/v1/user/current-token');
      const embedToken = response.data?.accessToken ?? null;
      const embedRefreshToken = response.data?.refreshToken ?? null;
      setIframeConfig({ url, title, token: embedToken, refreshToken: embedRefreshToken });
    } catch {
      // Fallback: open without token — embedded app handles its own auth
      setIframeConfig({ url, title, token: null, refreshToken: null });
    }
    setShowIframe(true);
  };

  useEffect(() => {
    //if lastclickedmenu exists in the treedata , expand it
    if (lastClickedMenu) {
      const foundNode = treeData.find(node => node.id === lastClickedMenu);
      if (foundNode) {
        setBaseParentId(foundNode?.id);
      }
    } else {
      // If there's no lastClickedMenu, set the baseParentId or initialize based on other criteria
      setBaseParentId(orgId === "superAdmin" ? 'organizations' : 'home');
    }
  }, [lastClickedMenu, treeData, orgId]);// Dependency on lastClickedMenu and treeData to react to changes

  useEffect(() => {
    const handleResize = () => {
      // Only force-collapse on mobile/tablet — never auto-expand, so a user
      // who manually collapsed the sidebar on desktop keeps their preference.
      if (window.innerWidth <= 1024) setCollapsed(true);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [setCollapsed]);

  const handleMenuItemSettings = useCallback(async () => {
    try {
      const response = await authAxios.get('/api/v1/menu/leftMenu');
      if (response.status === 200) {
        setTreeData(response.data.data);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Apologies for the inconvenience. We are currently addressing an unexpected error.", { autoClose: 5000 });
    }
  }, []);
  useEffect(() => {
    handleMenuItemSettings();
  }, [menuUpdate, handleMenuItemSettings]);

  //close popup when clicked outside
  useEffect(() => {
    const handleOutSideClick = (e) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(e.target) &&
        !sidebarRef.current.contains(e.target)
      ) {
        setShowProjectTag(false);
        setActiveProjectId(null);
        setSelectedProject(null);
        if (onMenuClose) onMenuClose();
      }
    };
    document.addEventListener("mousedown", handleOutSideClick);
    return () => document.removeEventListener("mousedown", handleOutSideClick);
  }, [onMenuClose]);

  useEffect(() => {
    setShowProjectTag(false);
    // setActiveProjectId(null);
    setSelectedProject(null);
    if (onMenuClose) onMenuClose();
  }, [location.pathname]);
  useEffect(() => {
    // Close popup UI
    setShowProjectTag(false);
    setSelectedProject(null);
    if (onMenuClose) onMenuClose();

    // try to find and activate the menu item matching the current pathname
    const findMenuByPath = (nodes, pathname) => {
      for (let node of nodes) {
        if (node.path === pathname) {
          return node;
        }
        if (node.children && node.children.length > 0) {
          const found = findMenuByPath(node.children, pathname);
          if (found) return found;
        }
      }
      return null;
    };

    const matchedNode = findMenuByPath(treeData, location.pathname);
    if (matchedNode) {
      // Found a matching menu item for the current path
      const rootNodeId = matchedNode.relation ? matchedNode.relation.split('/').filter(v => v !== '')[0] : matchedNode.id;
      setBaseParentId(rootNodeId);
      setActiveProjectId(null);
    } else if (lastClickedMenu) {
      // Fallback: Restore activeProjectId and baseParentId from Redux after navigation
      const foundNode = treeData.find(node => node.id === lastClickedMenu);
      if (foundNode) {
        // Check if it's a project node
        if (foundNode.type === 'project') {
          setActiveProjectId(foundNode.id);
          setBaseParentId(foundNode.id);
        } else {
          // If it's not a project, find its parent project
          setBaseParentId(foundNode.id);
        }
      }
    }
  }, [location.pathname, lastClickedMenu, treeData]);

  useEffect(() => {
    if (!collapsed) {
      // Sidebar expanded → popup should NOT exist
      setShowProjectTag(false);
      setSelectedProject(null);
      setActiveProjectId(null);

      // Move popup-expanded state back to sidebar if needed
      setExpandedNodes([]);
    } else {
      // Sidebar collapsed → tree should not stay expanded
      setExpandedNodes([]);
    }
  }, [collapsed]);

  const prevProjectRef = useRef(null);

  useEffect(() => {
    if (!collapsed) return;

    if (!prevProjectRef.current) {
      prevProjectRef.current = activeProjectId;
      return;
    }

    // Project changed → reset expanded apps
    if (prevProjectRef.current !== activeProjectId) {
      setExpandedNodes([]);
    }
    prevProjectRef.current = activeProjectId;
  }, [activeProjectId, collapsed]);

  const handleItemClick = useCallback(async (node, event) => {
    if (node.type === 'designapibuilder') {
      if (node.externalUrl) {
        sessionStorage.setItem('apibuilder_url', node.externalUrl);
      }
      navigate('/api-builder');
      if (onMenuClose) onMenuClose();
      return;
    }
    if (
      node.type === 'designpdfbuilder' ||
      node.relation === 'design/pdf-builder'
    ) {
      // Native PDF Builder (ported from the standalone report-builder app —
      // previously embedded via iframe with REACT_APP_REPORT_BUILDER_URL).
      navigate('/pdf-builder');
      if (onMenuClose) onMenuClose();
      return;
    }
    if (node.type === "project") {
      // If sidebar is NOT collapsed (expanded TreeView mode)
      if (!collapsed) {
        // Toggle expansion: if clicking the same active project, collapse it
        if (activeProjectId === node.id && expandedNodes.includes(node.id)) {
          // Close/collapse the project
          setExpandedNodes((prev) => prev.filter(id => id !== node.id));
          setActiveProjectId(null);
          setBaseParentId(null);
          return;
        }

        // Clear all expanded nodes and expand only the new project
        setExpandedNodes([node.id]);
        setSelectedProjectId(node.id);
        setActiveProjectId(node.id);
        setBaseParentId(node.id);
        return;
      }

      // If sidebar IS collapsed (popup mode)
      if (collapsed) {
        // Toggle popup: if clicking the same active project with popup open, close it
        if (activeProjectId === node.id && showProjectTag) {
          setShowProjectTag(false);
          setActiveProjectId(null);
          setSelectedProject(null);
          setBaseParentId(null);
          if (onMenuClose) onMenuClose();
          return;
        }

        // Otherwise, open popup for the project
        const rect = event.currentTarget.getBoundingClientRect();
        const viewportHeight = window.innerHeight;

        let top = rect.top + rect.height / 2;

        if (top + POPUP_HEIGHT / 2 > viewportHeight - POPUP_MARGIN) {
          top = viewportHeight - POPUP_HEIGHT / 2 - POPUP_MARGIN;
        }

        if (top - POPUP_HEIGHT / 2 < POPUP_MARGIN) {
          top = POPUP_HEIGHT / 2 + POPUP_MARGIN;
        }

        setPopupPosition({
          top,
          left: rect.right + 15,
        });

        setSelectedProject(node);
        setActiveProjectId(node.id);
        setBaseParentId(node.id);
        setShowProjectTag(true);
        return;
      }
    }

    // Handle design, setting, survey clicks (only in collapsed mode)
    if (["design", "setting"].includes(node.type)) {
      setExpandedNodes([]);
      setBaseParentId(null);
      // Toggle logic
      if (activeProjectId === node.id && showProjectTag) {
        setShowProjectTag(false);
        setActiveProjectId(null);
        setSelectedProject(null);
        if (onMenuClose) onMenuClose();
        return;
      }

      const rect = event.currentTarget.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      let top = rect.top + rect.height / 2;

      if (top + POPUP_HEIGHT / 2 > viewportHeight - POPUP_MARGIN) {
        top = viewportHeight - POPUP_HEIGHT / 2 - POPUP_MARGIN;
      }

      if (top - POPUP_HEIGHT / 2 < POPUP_MARGIN) {
        top = POPUP_HEIGHT / 2 + POPUP_MARGIN;
      }

      setPopupPosition({
        top,
        left: rect.right + 15,
      });

      setSelectedProject(node);
      setActiveProjectId(node.id);
      setShowProjectTag(true);
      return;
    }

    if (["survey"].includes(node.type)) {
      // Toggle logic
      if (activeProjectId === node.id && showProjectTag) {
        setShowProjectTag(false);
        setActiveProjectId(null);
        setSelectedProject(null);
        if (onMenuClose) onMenuClose();
        return;
      }

      const rect = event.currentTarget.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      let top = rect.top + rect.height / 2;

      if (top + POPUP_HEIGHT / 2 > viewportHeight - POPUP_MARGIN) {
        top = viewportHeight - POPUP_HEIGHT / 2 - POPUP_MARGIN;
      }

      if (top - POPUP_HEIGHT / 2 < POPUP_MARGIN) {
        top = POPUP_HEIGHT / 2 + POPUP_MARGIN;
      }

      setPopupPosition({
        top,
        left: rect.right + 15,
      });

      setSelectedProject(node);
      setActiveProjectId(node.id);
      setShowProjectTag(true);
      return;
    }

    //  Clear activeProjectId when clicking non-project items like "home"
    if (node.type !== "project" && node.type !== "survey") {
      setActiveProjectId(null);
    }

    // Any node that reaches this point is a plain navigational/leaf item
    // (all popup-opening types returned early above), so dismiss the popup.
    setShowProjectTag(false);
    setSelectedProject(null);
    if (onMenuClose) onMenuClose();

    // Rest of your navigation logic...
    setPreviousPath(expandedNodes);
    const path = node.relation;
    if (path) {
      const ids = path.split('/');
      if (previousPath.length === ids.length && previousPath.every((value, index) => value === ids[index])) {
        ids.pop();
        if (node?.children?.length > 0) {
          setExpandedNodes(ids);
        } else {
          ids.pop();
          setExpandedNodes(ids);
        }
      } else {
        if (node?.children?.length > 0) {
          setExpandedNodes(ids);
        } else {
          ids.pop();
          setExpandedNodes(ids);
        }
      }
    } else {
      setExpandedNodes([]);
    }

    // Navigation logic
    if (
      (node?.children?.length === 0 || node?.children === undefined) &&
      (node?.type !== 'project' && node?.type !== 'survey')
    ) {
      if (location.pathname !== node.path) {
        dispatch(setFilterData([]));
        dispatch(setSearchData(''));
        navigate(node.path);
        setBaseParentId(path.split('/').filter(value => value !== '')[0]);
        dispatch(setLastClickedMenu(path.split('/').filter(value => value !== '')[0]));
      } else {
        dispatch(setFilterData([]));
        dispatch(setSearchData(''));
        navigate('/refresh', { replace: true });
        setTimeout(() => {
          navigate(node.path);
        }, 0);
      }
    }
  }, [expandedNodes, previousPath, dispatch, navigate, location.pathname, collapsed, activeProjectId, showProjectTag, onMenuClose]);
  const renderTree = useCallback((nodes) => {

    return (
      <TreeItem
        key={nodes.id}
        nodeId={nodes.id}
        label={
          <div className={`menu-line ${nodes.id === baseParentId || activeProjectId === nodes.id ? 'active' : ''}`}>
            <div title={nodes.label} className={`menu-icon-fill ${baseParentId === nodes.id || activeProjectId === nodes.id ? "active" : ""}`}>
              <SVGIcon name={nodes.icon} className="menu-icon" />
            </div>
            <div title={nodes.label} className="menu-label">{nodes.label}</div>

            {!NO_ARROW_TYPES.includes(nodes.type) &&
              expandedNodes.includes(nodes.id) ?
              nodes?.children?.length > 0 && (nodes.id === baseParentId ?
                <SVGIcon name='arrowexpand' stroke="#3A3A3A" width={20} height={21} className="expand-icon" /> :
                <SVGIcon name='arrowexpand' stroke="#3A3A3A" width={20} height={21} className="expand-icon" />) :

              !NO_ARROW_TYPES.includes(nodes.type) && nodes?.children?.length > 0 && (nodes.id === baseParentId ?
                <SVGIcon name='arrowright' stroke="3A3A3A" width={20} height={21} className="expand-icon" /> :
                <SVGIcon name='arrowright' stroke="#3A3A3A" width={20} height={21} className="expand-icon" />
              )}
          </div>
        }
        onClick={(e) => handleItemClick(nodes, e)}
        selected={nodes.isSelected}
      >
        {Array.isArray(nodes.children)
          ? nodes.children.filter(child => !POPUP_ONLY_TYPES.includes(child.type))
            .map((node) => (
              <TreeItem
                key={node.id}
                nodeId={node.id}
                label={
                  <div className='sub-tree-label-outer'>
                    <div className={`tree-label `}>
                      <SVGIcon name={node.icon} className="custom-icon" />
                      <div className="label-text" title={node.label}>{node.label}</div>
                    </div>
                  </div>
                }
                onClick={(e) => handleItemClick(node, e)}
                selected={node.isSelected}
              />
            ))
          : null}
      </TreeItem>
    )
  }, [expandedNodes, baseParentId, activeProjectId, handleItemClick]);

  //logout functionality
  const submitHandler = async () => {
    setIsGlobalLoading(true);
    try {
      const response = await authAxios.post('api/v1/user/logout');
      if (response.status === 200) {
        //clear all relevant storages and cookies
        localStorage.clear();
        sessionStorage.clear();
        TokenService.removeUser();
        expiryMsgStore.current = null;
        dispatch(addLog(false));
        dispatch(resetSidebar());
        navigate('/login');
      }
    } catch (err) {
      // error silently swallowed; loader dismissed in finally
    } finally {
      setIsGlobalLoading(false);
    }
  }

  const handleAddNew = (id, parentProjectId) => {

    dispatch(setLastClickedMenu(parentProjectId));
    setSelectedProjectId(parentProjectId);
    setActiveProjectId(parentProjectId);
    // setBaseParentId(parentProjectId);    

    //  collapse popup if open
    if (id === '671203aa9ce02cdec308181d') {
      navigate(`/purchaseorder`, { state: { path: "new" } });
    } else if (id === "6712311d9ce02cdec3081cb0") {
      navigate(`/approvalrequest/add/${id}`);
    } else if (id === '671b2235b213167603ec7adf') {
      navigate(`/goodsreceipt`, { state: { path: "new" } });
    } else if (id === '671b23d94c243993ea84b23f') {
      navigate(`/issueofitems`, { state: { path: "new" } });
    } else if (id === '68cb940b2c745350115e1f5a') {
      navigate(`/farmercontract`, { state: { path: "new" } });
    } else if (id === '68bea10f104c999dda88afd6') {
      navigate(`/facontract`, { state: { path: "new" } });
    } else if (id === '68b681311a93932d6cb242a1') {
      navigate(`/staff`, { state: { path: "new" } });
    } else if(id === '68e7517bf2f88062e7c4f9ac') {
      navigate(`/transportcontract`, { state: { path: "new" } });
    }
    else if(id === '6a61b7bfb92400975a3515d0') {
      navigate(`/landleaseagreement`, { state: { path: "new" } });
    }
    else {
      navigate(`/formsubmission_new/add/${id}`, { state: { path: "new" } });
    }
  };
  if (showIframe) {
    return (
      <IframeViewer
        url={iframeConfig.url}
        title={iframeConfig.title}
        token={iframeConfig.token}
        refreshToken={iframeConfig.refreshToken}
        onBack={handleIframeBack}
      />
    );
  }

  return (
    <div ref={sidebarRef} role="navigation" aria-label="Main navigation" className={`sidebarnew-container ${collapsed ? "collapsed" : ""}`}>
      {/* Logo */}
      <div className="logo-section">
        {!collapsed &&
          (org_logo ? (
            <img src={org_logo} alt="Farmlogics" className="logofl-icon" />
          ) : (
            <img src={logoFarmlogics} alt="Farmlogics" className="logofl-icon" />
          ))
        }
        <div
          className="collapse-toggle-btn"
          role="button"
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          aria-expanded={!collapsed}
          tabIndex={0}
          onClick={() => setCollapsed((p) => !p)}
          onKeyDown={(e) => e.key === 'Enter' && setCollapsed((p) => !p)}
        >
          <SVGIcon
            name={collapsed ? "rightMenuBtn" : "leftmenuBtn"}
            className="menu-icon-top"
          />
        </div>
      </div>

      {/* Dynamic Menu Items (from API treeData) */}
      <div className="SidebarMenu-section">
        {!collapsed ? (

          <TreeView
            expanded={expandedNodes}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              '& .MuiTreeItem-content': { padding: '0 !important' },
              '& .MuiTreeItem-content:hover': { backgroundColor: 'transparent !important' },
              '& .MuiTreeItem-root .Mui-selected': { backgroundColor: 'transparent !important' },
              '& .MuiTreeItem-root .Mui-selected:hover': { backgroundColor: 'transparent !important' },
            }}
          >
            {treeData?.map((node) => renderTree(node))}
          </TreeView>
        ) : (
          treeData.map((node) => (
            <div
              key={node.id}
              role="button"
              tabIndex={0}
              className={`menu-line ${baseParentId === node.id || activeProjectId === node.id ? "active" : ""
                }`}
              onClick={(e) => handleItemClick(node, e)}
              onKeyDown={(e) => e.key === 'Enter' && handleItemClick(node, e)}
              title={node.label}
            >
              <div
                className={`menu-icon-fill ${baseParentId === node.id || activeProjectId === node.id ? "active" : ""
                  }`}
                title={node.label}
              >
                <SVGIcon name={node.icon} className="menu-icon" />
              </div>
            </div>
          ))
        )}
      </div>

      {/* Logout */}
      <div className="logout-section">
        <div
          className="logout-line"
          role="button"
          tabIndex={0}
          onClick={() => setShowLogoutConfirm(true)}
          onKeyDown={(e) => e.key === 'Enter' && setShowLogoutConfirm(true)}
          title="Logout"
        >
          <SVGIcon name="LogoutIcon" className="logout-icon" />
          {!collapsed && <span className="menu-label">Logout</span>}
        </div>
      </div>
      {showLogoutConfirm && (
        <ConfirmModal
          message="Are you sure you want to log out?"
          confirmText="Logout"
          cancelText="Cancel"
          onConfirm={() => {
            setShowLogoutConfirm(false);
            submitHandler();
          }}
          onCancel={() => setShowLogoutConfirm(false)}
        />
      )}
      {showProjectTag && (
        <div
          className="sidebar-dim-overlay"
          onClick={() => {
            setShowProjectTag(false);
            setActiveProjectId(null);
            setSelectedProject(null);
            if (onMenuClose) onMenuClose();
          }}
        />
      )}

      {/* Project Tag Popup */}
      {showProjectTag && selectedProject && selectedProject.type !== "menu" && ReactDOM.createPortal(
        <div
          ref={popupRef}
          className="project-tag-popup"
          style={{
            top: `${popupPosition.top}px`,
            left: `${popupPosition.left}px`,
            opacity: showProjectTag ? 1 : 0,
            transform: "translateY(-50%)",
            transition: "opacity 0.2s ease, transform 0.2s ease",
          }}
        >

          <div className="project-tag-scroll">
            <div className="project-tag-content">
              <div className="project-title" title={selectedProject.label}>
                <SVGIcon name={selectedProject.icon} className="menu-icon" stroke="var(--primary-color)" />
                <span>{selectedProject.label}</span>
              </div>
              <hr />
              {selectedProject.children?.map((app) => (
                <div key={app.id} className="project-app-section">
                  <div
                    className="project-item app-header"
                    title={app.label}
                    onClick={(e) => {
                      if (app.children?.length > 0) {
                        // expand/collapse
                        setExpandedNodes((prev) =>
                          prev.includes(app.id)
                            ? prev.filter((id) => id !== app.id)
                            : [...prev, app.id]
                        );
                      } else {
                        // direct navigation
                        handleItemClick(app, e);
                      }
                    }}
                  >
                    <SVGIcon name={app.icon} className="menu-icon" height={24} width={24} stroke="#3A3A3A" />
                    <span>{app.label}</span>
                    {app.children?.length > 0 && (
                      <SVGIcon
                        name={expandedNodes.includes(app.id) ? "upArrowIcon" : "downArrowIcon"}
                        className="icon-style"
                      />
                    )}
                    {!collapsed && !hideAddIcon && !["report", "actiontables"].includes(app?.model) && (
                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAddNew(app.id, selectedProject.id);
                        }}
                        className="popup-add-btn"
                      >
                        <SVGIcon name="AddIcon" className="icon-style" />
                      </span>
                    )}

                  </div>
                  {/* forms under each application */}
                  {expandedNodes.includes(app.id) &&
                    app.children?.map((form) => (
                      <div
                        key={form.id}
                        className="project-subitem"
                        title={form.label}
                        onClick={(e) => handleItemClick(form, e)}
                      >
                        <SVGIcon name={form.icon} className="menu-icon small" stroke="#3A3A3A" />
                        <span className="PopupForm-label">{form.label}</span>

                        {collapsed && form?.type !== "fgproject" && !["report", "actiontables"].includes(form?.model) && (
                          <span
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAddNew(form.id, selectedProject.id);
                            }}
                            className="popup-add-btn"
                          >
                            <SVGIcon name="AddIcon" className="icon-style" />
                          </span>
                        )}

                      </div>
                    ))}
                </div>
              ))}

            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default SidebarNew;